// src/pages/Affiliate.jsx
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
  Card, CardHeader, CardTitle, CardContent, CardDescription,
  Button, Input, Badge, Alert, AlertDescription,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty,
} from '../components/ui';
import { formatCurrency, formatDate, copyToClipboard } from '../lib/utils';
import {
  Share2, Copy, Users, DollarSign, Clock, CheckCircle, XCircle,
  AlertCircle, ChevronRight, Globe, Youtube, Mail, Mic,
  MessageSquare, Newspaper, ExternalLink,
} from 'lucide-react';
import toast from 'react-hot-toast';

const CHANNELS = [
  { value: 'blog',     label: 'Blog / SEO',       icon: Newspaper },
  { value: 'youtube',  label: 'YouTube',            icon: Youtube },
  { value: 'social',   label: 'Social Media',       icon: MessageSquare },
  { value: 'email',    label: 'Email Newsletter',   icon: Mail },
  { value: 'podcast',  label: 'Podcast',            icon: Mic },
  { value: 'other',    label: 'Other',              icon: Globe },
];

const AUDIENCE_SIZES = [
  { value: '0-500',   label: 'Under 500' },
  { value: '500-5k',  label: '500 – 5,000' },
  { value: '5k-50k',  label: '5,000 – 50,000' },
  { value: '50k+',    label: '50,000+' },
];

const PAYMENT_METHODS = [
  { value: 'paypal',        label: 'PayPal', hint: 'PayPal email address' },
  { value: 'bank_transfer', label: 'Bank Transfer', hint: 'IBAN / account number' },
  { value: 'crypto',        label: 'Crypto (USDT/BTC)', hint: 'Wallet address' },
];

// ── Application form (multi-step) ────────────────────────────────────────────
function ApplicationForm({ onSubmitted, profile }) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    website_url:       '',
    social_media_url:  '',
    audience_size:     '',
    audience_desc:     '',
    marketing_channels: [],
    marketing_plan:    '',
    payment_method:    '',
    payment_details:   '',
  });

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleChannel = (v) => {
    setForm(f => ({
      ...f,
      marketing_channels: f.marketing_channels.includes(v)
        ? f.marketing_channels.filter(c => c !== v)
        : [...f.marketing_channels, v],
    }));
  };

  const canNext1 = form.audience_size && form.audience_desc.trim().length >= 30 && form.marketing_channels.length > 0;
  const canNext2 = form.marketing_plan.trim().length >= 50;
  const canSubmit = form.payment_method && form.payment_details.trim().length > 3;

  const handleSubmit = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const code = `REV${(profile?.username || 'USER').toUpperCase().substring(0, 4)}${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
      const { data, error } = await supabase.from('affiliates').insert({
        user_id:            user.id,
        code:               code,
        referral_code:      code,
        status:             'pending',
        total_earned:       0,
        pending_amount:     0,
        paid_amount:        0,
        website_url:        form.website_url || null,
        social_media_url:   form.social_media_url || null,
        audience_size:      form.audience_size,
        audience_desc:      form.audience_desc,
        marketing_channels: form.marketing_channels,
        marketing_plan:     form.marketing_plan,
        payment_method:     form.payment_method,
        payment_details:    form.payment_details,
      }).select().single();

      if (error) throw error;
      toast.success('Application submitted! We\'ll review it within 30 days.');
      onSubmitted(data);
    } catch (e) {
      toast.error(e.message || 'Failed to submit application');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Hero */}
      <div className="text-center">
        <div className="mx-auto p-4 bg-primary/10 rounded-full w-fit mb-4">
          <Share2 className="h-12 w-12 text-primary" />
        </div>
        <h1 className="text-3xl font-bold mb-2">Affiliate Program</h1>
        <p className="text-muted-foreground">Earn 20% recurring commission on every subscriber you refer</p>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-4 text-center">
        {[
          { value: '20%', label: 'Commission', sub: 'recurring' },
          { value: '$50', label: 'Min Payout', sub: 'threshold' },
          { value: '30d', label: 'Review Time', sub: 'maximum' },
        ].map(s => (
          <div key={s.label} className="p-4 bg-muted/40 rounded-xl border border-border">
            <p className="text-3xl font-bold text-primary">{s.value}</p>
            <p className="text-sm font-medium">{s.label}</p>
            <p className="text-xs text-muted-foreground">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* Progress indicator */}
      <div className="flex items-center gap-2">
        {[1, 2, 3].map(n => (
          <div key={n} className="flex items-center flex-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors ${
              step > n ? 'bg-primary border-primary text-white' :
              step === n ? 'border-primary text-primary' :
              'border-border text-muted-foreground'
            }`}>
              {step > n ? <CheckCircle className="h-4 w-4" /> : n}
            </div>
            {n < 3 && <div className={`flex-1 h-0.5 mx-2 ${step > n ? 'bg-primary' : 'bg-border'}`} />}
          </div>
        ))}
      </div>
      <div className="flex justify-between text-xs text-muted-foreground px-0">
        <span>Audience</span>
        <span>Marketing Plan</span>
        <span>Payment</span>
      </div>

      {/* Step 1 — Audience */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Step 1 — Your Audience</CardTitle>
            <CardDescription>Tell us who you'll be referring to RevHapi</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <label className="block text-sm font-medium mb-2">Website or main online presence</label>
              <Input placeholder="https://yoursite.com or @yourhandle" value={form.website_url}
                onChange={e => update('website_url', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Social media profile (YouTube, Instagram, LinkedIn…)</label>
              <Input placeholder="https://youtube.com/c/..." value={form.social_media_url}
                onChange={e => update('social_media_url', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Audience / subscriber size <span className="text-red-500">*</span></label>
              <div className="grid grid-cols-2 gap-2">
                {AUDIENCE_SIZES.map(s => (
                  <button key={s.value} type="button" onClick={() => update('audience_size', s.value)}
                    className={`p-3 border rounded-lg text-sm font-medium text-left transition-colors ${form.audience_size === s.value ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:bg-muted/40'}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Describe your audience <span className="text-red-500">*</span>
                <span className="text-muted-foreground font-normal ml-1">(min 30 chars)</span>
              </label>
              <textarea
                value={form.audience_desc}
                onChange={e => update('audience_desc', e.target.value)}
                placeholder="e.g. I run a blog for digital marketers and media buyers who spend on Meta and Google Ads. My readers are typically agency owners and freelance marketers managing $5k–$100k/mo in ad spend…"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-card text-foreground h-28 resize-none"
              />
              <p className="text-xs text-muted-foreground mt-1">{form.audience_desc.length} / 30+ characters</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Marketing channels you'll use <span className="text-red-500">*</span></label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {CHANNELS.map(({ value, label, icon: Icon }) => (
                  <button key={value} type="button" onClick={() => toggleChannel(value)}
                    className={`flex items-center gap-2 p-3 border rounded-lg text-sm transition-colors ${form.marketing_channels.includes(value) ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:bg-muted/40 text-foreground'}`}>
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <Button className="w-full" onClick={() => setStep(2)} disabled={!canNext1}>
              Next — Marketing Plan <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 2 — Marketing Plan */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Step 2 — Marketing Plan</CardTitle>
            <CardDescription>How will you promote RevHapi to your audience?</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="p-4 bg-muted/40 rounded-lg border border-border text-sm space-y-1">
              <p className="font-medium">Things to cover:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
                <li>What type of content you'll create (reviews, tutorials, comparisons…)</li>
                <li>Where you'll place affiliate links or mention RevHapi</li>
                <li>How often you plan to promote</li>
                <li>Any existing audiences or email lists you'll reach out to</li>
              </ul>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Your marketing plan <span className="text-red-500">*</span>
                <span className="text-muted-foreground font-normal ml-1">(min 50 chars)</span>
              </label>
              <textarea
                value={form.marketing_plan}
                onChange={e => update('marketing_plan', e.target.value)}
                placeholder="e.g. I'll write a detailed comparison article of RevHapi vs competitors on my blog (25k monthly readers). I'll also include it in my weekly newsletter to 3,000 media buyers and create a YouTube tutorial showing how to connect Meta Ads and track ROAS…"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-card text-foreground h-40 resize-none"
              />
              <p className="text-xs text-muted-foreground mt-1">{form.marketing_plan.length} / 50+ characters</p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setStep(1)}>Back</Button>
              <Button className="flex-1" onClick={() => setStep(3)} disabled={!canNext2}>
                Next — Payment <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3 — Payment */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle>Step 3 — Payment Details</CardTitle>
            <CardDescription>How should we pay your commissions? (minimum $50, monthly)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <label className="block text-sm font-medium mb-2">Payment method <span className="text-red-500">*</span></label>
              <div className="space-y-2">
                {PAYMENT_METHODS.map(m => (
                  <button key={m.value} type="button" onClick={() => update('payment_method', m.value)}
                    className={`w-full flex items-center justify-between p-3 border rounded-lg text-sm transition-colors ${form.payment_method === m.value ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'}`}>
                    <span className="font-medium">{m.label}</span>
                    <span className="text-muted-foreground text-xs">{m.hint}</span>
                  </button>
                ))}
              </div>
            </div>
            {form.payment_method && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  {PAYMENT_METHODS.find(m => m.value === form.payment_method)?.hint} <span className="text-red-500">*</span>
                </label>
                <Input
                  value={form.payment_details}
                  onChange={e => update('payment_details', e.target.value)}
                  placeholder={PAYMENT_METHODS.find(m => m.value === form.payment_method)?.hint}
                />
              </div>
            )}
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                Applications are reviewed within <strong>30 days</strong>. We review all applications manually to ensure quality. You'll receive an email when your application is approved or rejected.
              </AlertDescription>
            </Alert>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setStep(2)}>Back</Button>
              <Button className="flex-1" onClick={handleSubmit} disabled={!canSubmit || saving} loading={saving}>
                Submit Application
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Pending screen ─────────────────────────────────────────────────────────────
function PendingScreen({ affiliate }) {
  const appliedDate = new Date(affiliate.created_at);
  const deadline = new Date(appliedDate);
  deadline.setDate(deadline.getDate() + 30);
  const daysLeft = Math.max(0, Math.ceil((deadline - new Date()) / 86400000));

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <Card>
        <CardContent className="pt-8 pb-8 text-center space-y-4">
          <div className="mx-auto p-4 bg-amber-500/10 rounded-full w-fit">
            <Clock className="h-12 w-12 text-amber-500" />
          </div>
          <div>
            <h2 className="text-2xl font-bold mb-1">Application Under Review</h2>
            <p className="text-muted-foreground">
              Your affiliate application was submitted on {formatDate(affiliate.created_at)} and is being reviewed by our team.
            </p>
          </div>
          <div className="bg-muted/40 rounded-xl p-5 text-left space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Submitted</span>
              <span className="font-medium">{formatDate(affiliate.created_at)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Decision deadline</span>
              <span className="font-medium">{formatDate(deadline.toISOString())}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Days remaining</span>
              <Badge variant={daysLeft <= 7 ? 'default' : 'secondary'}>{daysLeft} days</Badge>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            We review every application carefully. You'll receive an email at your registered address when a decision is made.
          </p>
          <Badge variant="warning" className="mx-auto">
            <Clock className="h-3 w-3 mr-1" />Pending Review
          </Badge>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Rejected screen ────────────────────────────────────────────────────────────
function RejectedScreen({ affiliate }) {
  return (
    <div className="max-w-lg mx-auto">
      <Card>
        <CardContent className="pt-8 pb-8 text-center space-y-4">
          <div className="mx-auto p-4 bg-red-500/10 rounded-full w-fit">
            <XCircle className="h-12 w-12 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold">Application Not Approved</h2>
          {affiliate.rejection_reason && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-left text-sm">
              <p className="font-medium mb-1 text-red-600">Reason:</p>
              <p className="text-muted-foreground">{affiliate.rejection_reason}</p>
            </div>
          )}
          <p className="text-muted-foreground text-sm">
            You may re-apply after 90 days if your circumstances have changed or if you've grown your audience further.
          </p>
          <Badge variant="destructive">Not Approved</Badge>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Approved dashboard ─────────────────────────────────────────────────────────
function AffiliateDashboard({ affiliate, referrals, onRefresh }) {
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const refCode = affiliate.code || affiliate.referral_code;
  const link = `${window.location.origin}/register?ref=${refCode}`;

  const handleCopyLink = () => {
    copyToClipboard(link);
    setCopyFeedback(true);
    setTimeout(() => setCopyFeedback(false), 2000);
    toast.success('Affiliate link copied!');
  };

  const pendingBalance = affiliate.pending_amount || affiliate.pending || 0;
  const canRequestPayout = pendingBalance >= 50;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Affiliate Dashboard</h1>
          <p className="text-muted-foreground text-sm">Track your referrals and earnings</p>
        </div>
        <Badge variant="success" className="flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />Approved
        </Badge>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Earned',   value: formatCurrency(affiliate.total_earned || 0), icon: DollarSign, color: 'text-primary' },
          { label: 'Pending',        value: formatCurrency(pendingBalance),               icon: Clock,      color: 'text-amber-500' },
          { label: 'Paid Out',       value: formatCurrency(affiliate.paid_amount || affiliate.paid || 0), icon: CheckCircle, color: 'text-green-500' },
          { label: 'Total Referrals',value: referrals.length,                            icon: Users,      color: 'text-blue-500' },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg bg-muted`}>
                  <s.icon className={`h-5 w-5 ${s.color}`} />
                </div>
                <div>
                  <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Payout banner */}
      {canRequestPayout && (
        <Alert className="border-green-500/30 bg-green-500/10">
          <DollarSign className="h-4 w-4 text-green-500" />
          <AlertDescription className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-sm">You have <strong>{formatCurrency(pendingBalance)}</strong> available for payout!</span>
            <Button size="sm" onClick={() => setShowPayoutModal(true)}>Request Payout</Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Affiliate link */}
      <Card>
        <CardHeader>
          <CardTitle>Your Affiliate Link</CardTitle>
          <CardDescription>Share this link — anyone who signs up earns you 20% of their subscription</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <input
              value={link}
              readOnly
              className="flex-1 px-3 py-2 border border-border rounded-lg bg-muted/40 text-sm font-mono text-foreground"
            />
            <Button onClick={handleCopyLink} variant={copyFeedback ? 'default' : 'outline'}>
              {copyFeedback ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm text-muted-foreground">Your code:</span>
            <Badge variant="outline" className="font-mono cursor-pointer select-all" onClick={() => { copyToClipboard(refCode); toast.success('Code copied!'); }}>
              {refCode} <Copy className="h-3 w-3 ml-2" />
            </Badge>
          </div>
          <div className="pt-2 border-t border-border">
            <p className="text-xs text-muted-foreground mb-2">Share on:</p>
            <div className="flex gap-2 flex-wrap">
              {[
                { label: 'Twitter/X', url: `https://twitter.com/intent/tweet?text=I%20use%20RevHapi%20to%20track%20my%20ad%20spend%20%26%20ROAS.%20Sign%20up%20here%3A%20${encodeURIComponent(link)}` },
                { label: 'LinkedIn',  url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}` },
              ].map(s => (
                <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="outline">
                    <ExternalLink className="h-3.5 w-3.5 mr-1" />{s.label}
                  </Button>
                </a>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Referrals table */}
      <Card>
        <CardHeader>
          <CardTitle>Your Referrals</CardTitle>
          <CardDescription>{referrals.length} total · commissions are confirmed once the referred user's subscription clears</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Commission</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {referrals.length === 0 ? (
                <TableEmpty colSpan={4} message="No referrals yet — share your link to start earning!" />
              ) : referrals.map(ref => (
                <TableRow key={ref.id}>
                  <TableCell className="font-medium">{formatDate(ref.created_at)}</TableCell>
                  <TableCell className="text-right font-semibold text-primary">{formatCurrency(ref.commission)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">{ref.subscription_tier || '—'}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={ref.status === 'paid' ? 'success' : ref.status === 'approved' ? 'default' : 'secondary'}>
                      {ref.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Payout info */}
      <Alert>
        <AlertDescription className="text-sm">
          Payouts are processed on the <strong>1st of each month</strong>. Minimum payout is <strong>$50</strong>.
          Payment method on file: <strong className="capitalize">{(affiliate.payment_method || '').replace('_', ' ') || 'not set'}</strong>.
          {!affiliate.payment_method && (
            <span className="text-amber-600 ml-1">Please contact support to update your payment details.</span>
          )}
        </AlertDescription>
      </Alert>

      {/* Payout modal */}
      {showPayoutModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Request Payout</CardTitle>
              <CardDescription>We'll process your payout on the 1st of next month</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted/40 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Available balance</span><span className="font-bold text-primary">{formatCurrency(pendingBalance)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Payment method</span><span className="capitalize">{(affiliate.payment_method || 'not set').replace('_', ' ')}</span></div>
                {affiliate.payment_details && (
                  <div className="flex justify-between"><span className="text-muted-foreground">Account</span><span className="font-mono text-xs">{affiliate.payment_details}</span></div>
                )}
              </div>
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  By requesting a payout, you confirm the payment details above are correct. Payouts are processed within 5 business days of the 1st.
                </AlertDescription>
              </Alert>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowPayoutModal(false)}>Cancel</Button>
                <Button className="flex-1" onClick={async () => {
                  // Insert payout request — admin handles manually
                  try {
                    await supabase.from('affiliate_payouts').insert({
                      affiliate_id: affiliate.id,
                      amount: pendingBalance,
                      payment_method: affiliate.payment_method,
                      payment_details: affiliate.payment_details,
                      status: 'requested',
                    });
                    toast.success('Payout requested! We\'ll process it on the 1st.');
                    setShowPayoutModal(false);
                  } catch (e) {
                    toast.error('Failed — please contact support');
                  }
                }}>
                  Confirm Request
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export const Affiliate = () => {
  const { user, profile } = useAuth();
  const [affiliate, setAffiliate]   = useState(null);
  const [referrals, setReferrals]   = useState([]);
  const [loading, setLoading]       = useState(true);

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: aff } = await supabase
        .from('affiliates').select('*').eq('user_id', user.id).maybeSingle();

      if (aff) {
        setAffiliate(aff);
        if (aff.status === 'approved') {
          const { data: refs } = await supabase
            .from('referrals').select('*').eq('affiliate_id', aff.id)
            .order('created_at', { ascending: false });
          setReferrals(refs || []);
        }
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );

  if (!affiliate) return <ApplicationForm onSubmitted={setAffiliate} profile={profile} />;
  if (affiliate.status === 'pending')  return <PendingScreen  affiliate={affiliate} />;
  if (affiliate.status === 'rejected') return <RejectedScreen affiliate={affiliate} />;
  return <AffiliateDashboard affiliate={affiliate} referrals={referrals} onRefresh={loadData} />;
};

export default Affiliate;
